import { onBeforeUnmount, type Ref, ref, watch, type WatchSource } from "vue";

/**
 * Measured on VoiceOver (macOS 26.6.2, Chrome, 2026-10-09): a live-region write that lands in the
 * same accessibility update as the focus change caused by disabling the control the user just
 * pressed is dropped, and a region whose text is set together with its mount is dropped too.
 * Writing the text one beat later lets the focus change settle first; 300 ms is the same deferral
 * ChatWaitingMessage carries. The text clears the moment the state ends, so a reader that was
 * mid-sentence is not left announcing a load that already finished. A retry should pass a
 * getter that yields different words from the first load: VoiceOver skipped a word-for-word
 * repeat of the previous announcement in the same region (measured the same day).
 */
export const LOADING_ANNOUNCEMENT_DELAY_MS = 300;

export function useLoadingAnnouncement(active: WatchSource<boolean>, text: string | (() => string)): Ref<string> {
    const announcement = ref("");
    let timer: ReturnType<typeof setTimeout> | null = null;
    const cancel = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };
    watch(
        active,
        (isActive) => {
            cancel();
            if (isActive) {
                timer = setTimeout(() => {
                    announcement.value = typeof text === "function" ? text() : text;
                    timer = null;
                }, LOADING_ANNOUNCEMENT_DELAY_MS);
            } else {
                announcement.value = "";
            }
        },
        { immediate: true },
    );
    onBeforeUnmount(cancel);
    return announcement;
}
